import type {
  BdVirtualClinicInsuranceType,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_VIRTUAL_CLINIC_INSURANCE_TYPE_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdEqualsCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_INSURANCE_TYPE_CONDITION_ID = 'insuranceType';
export const FilterConditionInsuranceTypePluginType: FilterConditionPluginSpecification<
  BdVirtualClinicInsuranceType | null,
  FilterConditionInsuranceTypeDto,
  BdInsuranceTypeCondition
> = {
  type: 'insuranceType',
  category: 'person',
  displayName: { de: 'Versicherungsart' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionInsuranceTypeDto,
  ): FilterConditionPluginImpl<BdVirtualClinicInsuranceType | null> {
    return new FilterConditionInsuranceTypePlugin(
        obj.insuranceType as BdVirtualClinicInsuranceType,
      );
  },
  fromCondition(
    jsonObj: BdInsuranceTypeCondition,
  ): FilterConditionPluginImpl<BdVirtualClinicInsuranceType | null> {
    return new FilterConditionInsuranceTypePlugin(jsonObj.equals);
  },
  newInstance(): FilterConditionPluginImpl<BdVirtualClinicInsuranceType | null> {
    return new FilterConditionInsuranceTypePlugin(null);
  },
};

export class FilterConditionInsuranceTypePlugin extends FilterConditionPluginImpl<
  BdVirtualClinicInsuranceType | null,
  FilterConditionInsuranceTypeDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    BdVirtualClinicInsuranceType | null,
    FilterConditionInsuranceTypeDto,
    BdInsuranceTypeCondition
  > = FilterConditionInsuranceTypePluginType;

  constructor(private insuranceType: BdVirtualClinicInsuranceType | null) {
    super(FilterConditionInsuranceTypePluginType.type);
  }

  clone(): FilterConditionPlugin<BdVirtualClinicInsuranceType> {
    return new FilterConditionInsuranceTypePlugin(this.insuranceType);
  }

  override equals(other: FilterConditionInsuranceTypePlugin): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this.insuranceType, other.insuranceType)
    );
  }

  override validationToText(): string {
    return this.valid() ? '' : 'Versicherungsart auswählen';
  }

  toCondition(): BdCondition | null {
    return new BdInsuranceTypeCondition(this.insuranceType);
  }

  toJSON(): FilterConditionInsuranceTypeDto | null {
    return {
      type: this.type,
      insuranceType: this.insuranceType,
    };
  }

  toText(): string | null {
    return 'Versicherungsart';
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    if (!this.insuranceType) return { title, items: [] };
    const displayName =
      BD_VIRTUAL_CLINIC_INSURANCE_TYPE_DISPLAY_NAMES[this.insuranceType] ??
      this.insuranceType;
    return { title, items: [displayName] };
  }

  toValue(): BdVirtualClinicInsuranceType | null {
    return this.insuranceType;
  }
}

export class BdInsuranceTypeCondition extends BdEqualsCondition<BdVirtualClinicInsuranceType> {
  constructor(insuranceType: BdVirtualClinicInsuranceType) {
    super(BD_INSURANCE_TYPE_CONDITION_ID, insuranceType);
  }
}

interface FilterConditionInsuranceTypeDto {
  type: string;
  insuranceType: string;
}
